import {
  getAccountNumberError,
  getPhoneNumberError,
  normalizePhoneNumber,
  validateRoutingNumber,
} from '@steerfi/shared';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useRef, useState } from 'react';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import {
  type AddressFormData,
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormControl,
  type FormErrorItem,
  FormErrorSummary,
  FormField,
  focusFirstInvalid,
} from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BusinessRegistrationFormProps {
  onBack: () => void;
}

type AchAccountType = 'checking' | 'savings';

interface FormData {
  // Personal info
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  // Business info
  businessName: string;
  // Address info
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  // ACH info
  achAccountHolderName: string;
  achRoutingNumber: string;
  achAccountNumber: string;
  achAccountType: AchAccountType;
  achBankName: string;
}

interface FormErrors {
  [key: string]: string;
}

const REG_FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Work Email',
  phoneNumber: 'Phone Number',
  password: 'Password',
  confirmPassword: 'Confirm Password',
  businessName: 'Business Name',
  address1: 'Street Address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP Code',
  achAccountHolderName: 'Account Holder Name',
  achRoutingNumber: 'Routing Number',
  achAccountNumber: 'Account Number',
  general: 'Form',
};

function errorsToSummaryItems(errors: FormErrors): FormErrorItem[] {
  return Object.entries(errors)
    .filter(([key]) => key !== 'general')
    .map(([key, message]) => ({
      label: REG_FIELD_LABELS[key] ?? key,
      message,
    }));
}

export function BusinessRegistrationForm({ onBack }: BusinessRegistrationFormProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    addressVerificationStatus: 'not_checked',
    achAccountHolderName: '',
    achRoutingNumber: '',
    achAccountNumber: '',
    achAccountType: 'checking',
    achBankName: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Address verification state
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address field changes
  const handleAddressChange = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset verification status when address changes
    if (formData.addressVerificationStatus !== 'not_checked') {
      setFormData((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address verification result
  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    // Show dialog if there are corrections or if verification failed
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  // Handle accepting suggested address
  const handleAcceptSuggestedAddress = (suggested: StandardizedAddress) => {
    setFormData((prev) => ({
      ...prev,
      address1: suggested.streetAddress,
      address2: suggested.secondaryAddress || '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);
  };

  // Handle keeping original address
  const handleKeepOriginalAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validateStep1 = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      nextErrors.firstName = 'Enter a first name';
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = 'Enter a last name';
    }
    if (!formData.email.trim()) {
      nextErrors.email = 'Enter an email like name@company.com';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Enter an email like name@company.com';
    }
    const phoneError = getPhoneNumberError(formData.phoneNumber);
    if (phoneError) {
      nextErrors.phoneNumber = phoneError;
    }
    if (!formData.password) {
      nextErrors.password = 'Enter a password with at least 8 characters';
    } else if (formData.password.length < 8) {
      nextErrors.password = 'Enter a password with at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = 'Re-enter the same password in both fields';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.businessName.trim()) {
      nextErrors.businessName = 'Enter a business name';
    }

    // Address validation
    if (!formData.address1.trim()) {
      nextErrors.address1 = 'Enter a street address';
    }
    if (!formData.city.trim()) {
      nextErrors.city = 'Enter a city';
    }
    if (!formData.state) {
      nextErrors.state = 'Select a state';
    }
    if (!formData.zipCode.trim()) {
      nextErrors.zipCode = 'Enter a ZIP code';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      nextErrors.zipCode = 'Enter a 5-digit ZIP or ZIP+4 like 10001-1234';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.achAccountHolderName.trim()) {
      nextErrors.achAccountHolderName = 'Enter the account holder name';
    }

    // Use improved routing number validation with detailed error messages
    const routingValidation = validateRoutingNumber(formData.achRoutingNumber);
    if (!routingValidation.isValid && routingValidation.message) {
      nextErrors.achRoutingNumber = routingValidation.message;
    }

    // Use improved account number validation with detailed error messages
    const accountError = getAccountNumberError(formData.achAccountNumber);
    if (accountError) {
      nextErrors.achAccountNumber = accountError;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const leaveStep = (step: 1 | 2) => {
    setErrors({});
    setSubmitAttempt(0);
    setCurrentStep(step);
  };

  const handleNextFromStep1 = () => {
    if (validateStep1()) {
      setSubmitAttempt(0);
      setCurrentStep(2);
    } else {
      setSubmitAttempt((n) => n + 1);
    }
  };

  const handleNextFromStep2 = () => {
    if (validateStep2()) {
      // Default account holder name to business name if not set
      if (!formData.achAccountHolderName.trim()) {
        setFormData((prev) => ({
          ...prev,
          achAccountHolderName: prev.businessName,
        }));
      }
      setSubmitAttempt(0);
      setCurrentStep(3);
    } else {
      setSubmitAttempt((n) => n + 1);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateStep3()) {
      setSubmitAttempt((n) => n + 1);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch('/api/auth/register/business', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phoneNumber: normalizePhoneNumber(formData.phoneNumber),
          password: formData.password,
          businessName: formData.businessName.trim(),
          // Address fields
          address1: formData.address1.trim(),
          address2: formData.address2.trim() || null,
          city: formData.city.trim(),
          state: formData.state,
          zipCode: formData.zipCode,
          addressVerificationStatus: formData.addressVerificationStatus,
          // ACH fields
          achAccountHolderName: formData.achAccountHolderName.trim(),
          achRoutingNumber: formData.achRoutingNumber,
          achAccountNumber: formData.achAccountNumber,
          achAccountType: formData.achAccountType,
          achBankName: formData.achBankName.trim() || null,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Redirect to registration success page
        window.location.href = '/registration-success?registered=true';
      } else {
        if (response.status === 409) {
          const message = data.message?.toLowerCase() || '';
          if (message.includes('email') || message.includes('account')) {
            setErrors({
              email: 'An account with this email already exists. Please log in instead.',
            });
            setCurrentStep(1);
            setSubmitAttempt((n) => n + 1);
            return;
          }
        }

        // Handle field-specific errors (e.g., duplicate business name)
        if (data.errors && typeof data.errors === 'object') {
          const fieldErrors: FormErrors = {};

          // Convert array errors to string
          for (const [key, value] of Object.entries(data.errors)) {
            if (Array.isArray(value) && value.length > 0) {
              fieldErrors[key] = value[0];
            } else if (typeof value === 'string') {
              fieldErrors[key] = value;
            }
          }

          setErrors(fieldErrors);

          // If there's a business name error, go back to step 2
          if (fieldErrors.businessName) {
            setCurrentStep(2);
          }
        } else {
          setErrors({
            general:
              data.message || 'Registration failed. Please check your details and try again.',
          });
        }
        setSubmitAttempt((n) => n + 1);
      }
    } catch (_error) {
      setErrors({
        general: 'An unexpected error occurred during registration. Please try again.',
      });
      setSubmitAttempt((n) => n + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <Button
          variant="ghost"
          onClick={() => {
            if (currentStep === 1) {
              onBack();
            } else {
              leaveStep(currentStep === 3 ? 2 : 1);
            }
          }}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle as="h1" className="text-2xl">
              Business Registration
            </CardTitle>
            <CardDescription>
              {currentStep === 1 &&
                'Step 1 of 3: Your personal information for the primary account owner'}
              {currentStep === 2 && 'Step 2 of 3: Business information and address'}
              {currentStep === 3 && 'Step 3 of 3: Business ACH account information'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate>
              <FormErrorSummary
                errors={errorsToSummaryItems(errors)}
                attempt={submitAttempt}
                onAnnounced={() => focusFirstInvalid(formRef.current)}
              />
              {currentStep === 1 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField label="First Name" required error={errors.firstName}>
                      <FormControl>
                        <Input
                          placeholder="Jane"
                          value={formData.firstName}
                          onChange={(event) => handleChange('firstName', event.target.value)}
                        />
                      </FormControl>
                    </FormField>
                    <FormField label="Last Name" required error={errors.lastName}>
                      <FormControl>
                        <Input
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={(event) => handleChange('lastName', event.target.value)}
                        />
                      </FormControl>
                    </FormField>
                  </div>

                  <FormField label="Work Email" required error={errors.email}>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="jane.doe@business.com"
                        value={formData.email}
                        onChange={(event) => handleChange('email', event.target.value)}
                      />
                    </FormControl>
                  </FormField>

                  <FormField label="Phone Number" required error={errors.phoneNumber}>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="+1 234 567 8900"
                        value={formData.phoneNumber}
                        onChange={(event) => handleChange('phoneNumber', event.target.value)}
                      />
                    </FormControl>
                  </FormField>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      label="Password"
                      required
                      error={errors.password}
                      description="At least 8 characters"
                    >
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={(event) => handleChange('password', event.target.value)}
                            className="pr-10"
                          />
                        </FormControl>
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormField>
                    <FormField label="Confirm Password" required error={errors.confirmPassword}>
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={formData.confirmPassword}
                            onChange={(event) =>
                              handleChange('confirmPassword', event.target.value)
                            }
                            className="pr-10"
                          />
                        </FormControl>
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={
                            showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormField>
                  </div>

                  <Button type="button" className="w-full" size="lg" onClick={handleNextFromStep1}>
                    Next: Business Information
                  </Button>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <FormField label="Business Name" required error={errors.businessName}>
                    <FormControl>
                      <Input
                        placeholder="Acme, Inc."
                        value={formData.businessName}
                        onChange={(event) => handleChange('businessName', event.target.value)}
                      />
                    </FormControl>
                  </FormField>

                  {/* Business Address Section */}
                  <div className="pt-4 border-t">
                    <h2 className="text-lg font-medium mb-4">Business Address</h2>
                    <AddressFormFields
                      address={{
                        address1: formData.address1,
                        address2: formData.address2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zipCode,
                      }}
                      errors={errors}
                      onChange={handleAddressChange}
                      onVerificationResult={handleAddressVerificationResult}
                      onVerificationStatusChange={(status) =>
                        setFormData((prev) => ({ ...prev, addressVerificationStatus: status }))
                      }
                    />
                  </div>

                  <Alert>
                    <AlertDescription>
                      We&apos;ll use this information to create your company profile and attach your
                      ACH account for payments. Your address will be verified with USPS.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => leaveStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      className="flex-1"
                      size="lg"
                      onClick={handleNextFromStep2}
                    >
                      Next: ACH Details
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <Alert>
                    <AlertDescription>
                      Your business ACH details will be encrypted using a secure key from AWS
                      Secrets Manager before being stored. Only masked information will be displayed
                      in the UI.
                    </AlertDescription>
                  </Alert>

                  <FormField
                    label="Account Holder Name"
                    required
                    error={errors.achAccountHolderName}
                  >
                    <FormControl>
                      <Input
                        placeholder={formData.businessName || 'Acme, Inc.'}
                        value={formData.achAccountHolderName}
                        onChange={(event) =>
                          handleChange('achAccountHolderName', event.target.value)
                        }
                      />
                    </FormControl>
                  </FormField>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      label="Routing Number"
                      required
                      error={errors.achRoutingNumber}
                      description="9 digits"
                    >
                      <FormControl>
                        <Input
                          placeholder="123456789"
                          maxLength={9}
                          value={formData.achRoutingNumber}
                          onChange={(event) =>
                            handleChange('achRoutingNumber', event.target.value.replace(/\D/g, ''))
                          }
                        />
                      </FormControl>
                    </FormField>
                    <FormField
                      label="Account Number"
                      required
                      error={errors.achAccountNumber}
                      description="4-17 digits"
                    >
                      <FormControl>
                        <Input
                          placeholder="1234567890"
                          maxLength={17}
                          value={formData.achAccountNumber}
                          onChange={(event) =>
                            handleChange('achAccountNumber', event.target.value.replace(/\D/g, ''))
                          }
                        />
                      </FormControl>
                    </FormField>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField label="Account Type" required>
                      <Select
                        value={formData.achAccountType}
                        onValueChange={(value: AchAccountType) =>
                          handleChange('achAccountType', value)
                        }
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select account type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="checking">Business Checking</SelectItem>
                          <SelectItem value="savings">Business Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Bank Name (Optional)">
                      <FormControl>
                        <Input
                          placeholder="Chase Bank"
                          value={formData.achBankName}
                          onChange={(event) => handleChange('achBankName', event.target.value)}
                        />
                      </FormControl>
                    </FormField>
                  </div>

                  {errors.general && (
                    <Alert variant="destructive">
                      <AlertDescription>{errors.general}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => leaveStep(2)}
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating Account...' : 'Create Business Account'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Address Verification Dialog */}
        <AddressVerificationDialog
          open={showAddressDialog}
          onOpenChange={setShowAddressDialog}
          verificationResult={addressVerificationResult}
          onAcceptSuggested={handleAcceptSuggestedAddress}
          onKeepOriginal={handleKeepOriginalAddress}
        />
      </div>
    </div>
  );
}
